import { useCallback, useEffect, useId, useRef, useState } from "react";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface AgentOption {
  id: string;
  label: string;
  email: string;
}

interface AgentSearchSelectProps {
  value: string;
  onChange: (agentId: string) => void;
  token: string;
  placeholder?: string;
  className?: string;
  excludeAgentId?: string;
  allowClear?: boolean;
  clearLabel?: string;
}

export function AgentSearchSelect({
  value,
  onChange,
  token,
  placeholder = "Filter by upline…",
  className = "",
  excludeAgentId,
  allowClear = true,
  clearLabel = "All uplines",
}: AgentSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [options, setOptions] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const listboxId = `${generatedId}-listbox`;
  const hasClearOption = Boolean(allowClear && value);

  const selectedOption = options.find((option) => option.id === value);

  const loadOptions = useCallback(
    async (search: string) => {
      if (!token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: "1", pageSize: "25" });
        if (search.trim()) params.set("search", search.trim());
        const res = await fetch(`${API_BASE}/api/agents?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) return;
        const items = (json.data?.items ?? []) as Array<{
          id: string;
          email: string;
          displayName: string | null;
        }>;
        setOptions(
          items
            .filter((item) => item.id !== excludeAgentId)
            .map((item) => ({
              id: item.id,
              email: item.email,
              label: item.displayName || item.email,
            }))
        );
      } finally {
        setLoading(false);
      }
    },
    [token, excludeAgentId]
  );

  useEffect(() => {
    if (!value || !token) {
      setSelectedLabel(null);
      return;
    }
    if (selectedOption) {
      setSelectedLabel(selectedOption.label);
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await fetch(`${API_BASE}/api/agents/${value}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (cancelled || !res.ok || !json.success) return;
      const agent = json.data as { displayName?: string | null; email: string };
      setSelectedLabel(agent.displayName || agent.email);
    })();

    return () => {
      cancelled = true;
    };
  }, [value, token, selectedOption]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void loadOptions(query);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [open, query, loadOptions]);

  const handleSelect = useCallback(
    (option: AgentOption | null) => {
      onChange(option?.id ?? "");
      setSelectedLabel(option?.label ?? null);
      setQuery("");
      setOpen(false);
      setActiveIdx(-1);
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        setOpen(true);
        setActiveIdx(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIdx((index) => Math.min(index + 1, options.length + (hasClearOption ? 1 : 0) - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((index) => Math.max(index - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" && activeIdx === 0 && hasClearOption) {
      handleSelect(null);
      e.preventDefault();
    } else if (e.key === "Enter" && activeIdx >= 0) {
      handleSelect(options[activeIdx - (hasClearOption ? 1 : 0)]);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setActiveIdx(-1);
    }
  };

  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
        setActiveIdx(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayLabel = selectedLabel ?? selectedOption?.label;

  return (
    <div ref={containerRef} className={`relative min-w-[220px] ${className}`}>
      <div
        className="relative cursor-pointer"
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search agents…"
            className="w-full rounded-lg border border-accent bg-surface-input px-3 py-2 text-sm text-content-primary placeholder:text-content-muted ring-2 ring-accent/20 focus:outline-none pr-11"
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeIdx >= 0 ? `${generatedId}-option-${activeIdx}` : undefined}
          />
        ) : (
          <button type="button" onKeyDown={handleKeyDown} aria-haspopup="listbox" aria-expanded="false" className="flex w-full items-center justify-between rounded-lg border border-border-default bg-surface-input px-3 py-2 text-sm transition-colors hover:border-accent">
            <span className={displayLabel ? "truncate text-content-primary" : "text-content-muted"}>
              {displayLabel || placeholder}
            </span>
            <svg className="h-4 w-4 shrink-0 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
          </button>
        )}

        {open && (
          <button
            type="button"
            className="absolute right-0 top-1/2 min-h-11 min-w-11 -translate-y-1/2 text-content-muted hover:text-content-primary"
            aria-label="Close agent options"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              setQuery("");
            }}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          className="absolute z-50 mt-1.5 max-h-60 w-full overflow-y-auto rounded-xl border border-border-default bg-surface-raised py-1 shadow-card-hover"
          role="listbox"
        >
          {allowClear && value && (
            <li
              role="option"
              id={`${generatedId}-option-0`}
              aria-selected={!value}
              className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                activeIdx === 0 ? "bg-accent-strong text-on-accent" : "text-content-secondary hover:bg-surface-overlay"
              }`}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelect(null);
              }}
              onMouseEnter={() => setActiveIdx(0)}
            >
              {clearLabel}
            </li>
          )}
          {loading && (
            <li className="px-3 py-2 text-sm text-content-muted" role="presentation">Loading agents…</li>
          )}
          {!loading && options.length === 0 && (
            <li className="px-3 py-2 text-sm text-content-muted" role="presentation">No agents found</li>
          )}
          {!loading &&
            options.map((option, index) => {
              const listIndex = index + (hasClearOption ? 1 : 0);
              return (
                <li
                  key={option.id}
                  id={`${generatedId}-option-${listIndex}`}
                  role="option"
                  aria-selected={option.id === value}
                  className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                    listIndex === activeIdx
                      ? "bg-accent-strong text-on-accent"
                      : option.id === value
                        ? "bg-accent-muted text-content-primary"
                        : "text-content-secondary hover:bg-surface-overlay"
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelect(option);
                  }}
                  onMouseEnter={() => setActiveIdx(listIndex)}
                >
                  <p className="font-medium">{option.label}</p>
                  {option.label !== option.email && (
                    <p className="text-xs opacity-70">{option.email}</p>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
